import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { formatDateTime } from '../lib/datetime'
import { effectiveStatus } from '../lib/status'

interface Tournament {
  id: string
  name: string
  course: string | null
  season: number
  major: string | null
  status: string
  starts_at: string
  lock_time: string
  current_round: number
}

const MAJOR_NAMES: Record<string, string> = {
  masters: 'Masters',
  pga: 'PGA Championship',
  us_open: 'US Open',
  the_open: 'The Open',
}

const STATUS_TEXT: Record<string, string> = {
  open: 'Anmälan öppen',
  locked: 'Laginlämningen stängd',
  live: 'Pågår',
  completed: 'Avslutad',
}

/**
 * Aktiv = något händer just nu som man kan missa. En pågående tävling
 * förstås, men också en öppen anmälan — den har en deadline och är det man
 * faktiskt riskerar att glömma. De två sorteras först och ramas in;
 * resten av säsongen står kronologiskt under.
 */
function arAktiv(t: Tournament) {
  const s = effectiveStatus(t)
  return s === 'live' || s === 'open' || s === 'locked'
}

export function Home() {
  const { profile } = useAuth()
  const [tournaments, setTournaments] = useState<Tournament[]>([])
  const [season, setSeason] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    supabase
      .from('tournaments')
      .select('id, name, course, season, major, status, starts_at, lock_time, current_round')
      .order('starts_at', { ascending: true })
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setTournaments((data as Tournament[]) ?? [])
        setLoading(false)
      })
  }, [])

  const seasons = useMemo(
    () => [...new Set(tournaments.map((t) => t.season))].sort((a, b) => b - a),
    [tournaments],
  )

  // Innevarande år är förvalt. Saknas det helt — i januari innan årets
  // majors lagts upp, eller under uppbyggnaden inför 2027 — faller valet
  // tillbaka på den senaste säsong som faktiskt har något att visa, i
  // stället för på en tom sida.
  useEffect(() => {
    if (season !== null || seasons.length === 0) return
    const iAr = new Date().getFullYear()
    setSeason(seasons.includes(iAr) ? iAr : (seasons[0] ?? null))
  }, [seasons, season])

  const visade = useMemo(() => {
    const iSasongen = tournaments.filter((t) => t.season === season)
    // Aktiva först, resten kronologiskt. Sorteringen är stabil i JS, så
    // ordningen från frågan (starts_at stigande) håller inom varje grupp.
    return [...iSasongen].sort((a, b) => Number(arAktiv(b)) - Number(arAktiv(a)))
  }, [tournaments, season])

  const antalAktiva = visade.filter(arAktiv).length

  return (
    <div className="page stack">
      <div>
        <p className="eyebrow">
          {profile ? `Inloggad som ${profile.display_name}` : 'Välkommen'}
        </p>
        <h1>Säsongen{season !== null ? ` ${season}` : ''}</h1>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <p className="muted">Hämtar turneringar…</p>}

      {!loading && tournaments.length === 0 && (
        <div className="card">
          <p className="muted">
            Inga turneringar upplagda än. En admin lägger upp majorerna inför säsongen,
            med regeluppsättning och tider.
          </p>
        </div>
      )}

      {seasons.length > 1 && (
        <div className="card">
          <div style={{ maxWidth: 160 }}>
            <label htmlFor="hemsasong">Säsong</label>
            <select
              id="hemsasong"
              value={season ?? ''}
              onChange={(e) => setSeason(Number(e.target.value))}
            >
              {seasons.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {!loading && tournaments.length > 0 && visade.length === 0 && (
        <div className="card">
          <p className="muted">Inga turneringar den här säsongen.</p>
        </div>
      )}

      {visade.map((t, i) => {
        const aktiv = arAktiv(t)
        // Klock-härledd status: databasen kan ligga upp till en minut efter
        // (workern flyttar den), och texten får inte säga "Anmälan öppen"
        // när låset passerats.
        const status = effectiveStatus(t)
        return (
          <article
            key={t.id}
            className={`card stack${aktiv ? ' card--aktiv' : ''}`}
          >
            {/* Rubriken sätts en gång, ovanför den första aktiva och den
                första som inte är det, så att gränsen mellan "nu" och
                "resten av säsongen" syns utan att varje kort bär en. */}
            {i === 0 && aktiv && <p className="eyebrow eyebrow--aktiv">Pågår nu</p>}
            {i === antalAktiva && antalAktiva > 0 && (
              <p className="eyebrow">Resten av säsongen</p>
            )}

            <div>
              <p className="eyebrow">
                {t.major ? `${MAJOR_NAMES[t.major] ?? t.major} ${t.season}` : `Tävling ${t.season}`}
              </p>
              <h2>{t.name}</h2>
              {t.course && <p className="muted">{t.course}</p>}
            </div>

            <p className={aktiv ? 'status status--aktiv' : 'muted'}>
              {STATUS_TEXT[status] ?? status}
              {status === 'live' && t.current_round > 0 && ` · rond ${t.current_round}`}
              {status === 'open' && ` · stänger ${formatDateTime(t.lock_time)}`}
              {status === 'locked' && ` · startar ${formatDateTime(t.starts_at)}`}
            </p>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {status === 'open' && (
                <Link to={`/turnering/${t.id}/lag`}>
                  <button>Drafta ditt lag</button>
                </Link>
              )}
              {status !== 'draft' && (
                <Link to={`/turnering/${t.id}/leaderboard`}>
                  <button>Leaderboard</button>
                </Link>
              )}
              {/* Inlämningsläget är intressant före start — det är då man jagar
                  folk. Draftboarden först när lagen avslöjats vid tee-off. */}
              {status !== 'draft' && (
                <Link to={`/turnering/${t.id}/inlamnade`}>
                  <button>Inlämnade lag</button>
                </Link>
              )}
              {(status === 'live' || status === 'completed') && (
                <Link to={`/turnering/${t.id}/draftboard`}>
                  <button>Draftboard</button>
                </Link>
              )}
              {status !== 'draft' && (
                <Link to={`/turnering/${t.id}/stryktips`}>
                  <button>Stryktips</button>
                </Link>
              )}
            </div>
          </article>
        )
      })}
    </div>
  )
}
